import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

/** `HH:MM` on a 24-hour clock, which is what the field stores. */
function parse(value: unknown): { h: number; m: number } | null {
  const s = String(value ?? '').slice(0, 5);
  const t = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!t) return null;
  const h = Number(t[1]);
  const m = Number(t[2]);
  if (h > 23 || m > 59) return null;
  return { h, m };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "09:05" becomes "09:05 AM", which is how the office says it. */
function human(h: number, m: number) {
  const suffix = h < 12 ? 'AM' : 'PM';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${pad(twelve)}:${pad(m)} ${suffix}`;
}

/**
 * A time field in this product's own clothes.
 *
 * The last control the browser still drew for itself. On Windows it is a
 * "--:-- --" skeleton with a small grey clock, on a phone a spinning wheel,
 * and beside the date field — which this product does draw — it was plainly
 * the odd one out.
 *
 * Like the date field it keeps a hidden native input and reports changes
 * through it, so every caller that reads `e.target.value` and every form
 * registered with react-hook-form keeps working untouched.
 */
export const TimeField = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }
>(({ className, value, defaultValue, onChange, disabled, error, placeholder, ...props }, ref) => {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(String(value ?? defaultValue ?? ''));
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);

  const picked = useMemo(() => parse(current), [current]);

  function setRef(el: HTMLInputElement | null) {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
  }

  useEffect(() => {
    const el = innerRef.current;
    if (el && el.value !== current) setCurrent(el.value);
  });
  useEffect(() => { if (value !== undefined) setCurrent(String(value ?? '')); }, [value]);

  function place() {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const PANEL = 260;
    const openUp = window.innerHeight - b.bottom < PANEL && b.top > window.innerHeight - b.bottom;
    setRect({
      top: openUp ? Math.max(8, b.top - PANEL - 4) : b.bottom + 4,
      left: Math.min(b.left, Math.max(8, window.innerWidth - 232)),
    });
  }
  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    }
    function away(e?: Event) {
      if (e && e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [open]);

  function commit(next: string) {
    const el = innerRef.current;
    if (el) {
      el.value = next;
      onChange?.({ target: el, currentTarget: el } as unknown as React.ChangeEvent<HTMLInputElement>);
    }
    setCurrent(next);
  }

  const h = picked?.h ?? 9;
  const m = picked?.m ?? 0;
  const setHour = (nh: number) => commit(`${pad(nh)}:${pad(m)}`);
  const setMinute = (nm: number) => commit(`${pad(h)}:${pad(nm)}`);
  const setHalf = (pm: boolean) => commit(`${pad(pm ? (h % 12) + 12 : h % 12)}:${pad(m)}`);

  /** A column of choices, the current one kept in view. */
  const column = (items: number[], selected: number, onPick: (n: number) => void, label: string) => (
    <ul className="max-h-48 flex-1 overflow-y-auto" aria-label={label}>
      {items.map((n) => (
        <li key={n}>
          <button
            type="button"
            ref={n === selected ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
            onClick={() => onPick(n)}
            className={cn('w-full rounded px-2 py-1.5 text-center text-[13px] tabular hover:bg-accent',
              n === selected && 'bg-primary font-semibold text-primary-foreground hover:bg-primary')}
          >
            {pad(n)}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div className={cn('relative', className)}>
      <input ref={setRef} type="time" value={current} onChange={() => {}} disabled={disabled}
        tabIndex={-1} aria-hidden className="sr-only" {...props} />

      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn('input flex items-center justify-between pr-2.5 text-left', error && 'input-error')}
      >
        <span className={cn('truncate', !picked && 'text-ink-400 dark:text-ink-500')}>
          {picked ? human(picked.h, picked.m) : (placeholder || 'Pick a time')}
        </span>
        <Clock className="ml-2 h-4 w-4 shrink-0 text-ink-400" />
      </button>

      {open && rect && createPortal(
        <div ref={panelRef} role="dialog"
          style={{ position: 'fixed', top: rect.top, left: rect.left, width: 224, zIndex: 9999 }}
          className="rounded-md border border-border bg-card p-2 shadow-lg">
          <div className="mb-1 flex gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <div className="flex-1">Hour</div><div className="flex-1">Minute</div><div className="w-12">&nbsp;</div>
          </div>
          <div className="flex gap-1">
            {column(Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i)).sort((a, b) => a - b),
              h % 12 === 0 ? 12 : h % 12,
              (n) => setHour(h < 12 ? (n === 12 ? 0 : n) : (n === 12 ? 12 : n + 12)), 'Hour')}
            {column(Array.from({ length: 60 }, (_, i) => i), m, setMinute, 'Minute')}
            <div className="flex w-12 flex-col gap-1">
              {(['AM', 'PM'] as const).map((s) => (
                <button key={s} type="button" onClick={() => setHalf(s === 'PM')}
                  className={cn('rounded px-1 py-1.5 text-[12px] font-medium hover:bg-accent',
                    (s === 'PM') === (h >= 12) && 'bg-primary text-primary-foreground hover:bg-primary')}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <button type="button" className="btn-ghost btn-sm"
              onClick={() => { const d = new Date(); commit(`${pad(d.getHours())}:${pad(d.getMinutes())}`); setOpen(false); }}>
              Now
            </button>
            <button type="button" className="btn-ghost btn-sm"
              onClick={() => { commit(''); setOpen(false); }}>
              Clear
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
});
TimeField.displayName = 'TimeField';
